import { ref, computed, watch, onMounted, onUnmounted } from "vue"; // onMounted, onUnmounted 추가
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { firebaseAuth, getCallable, storageRef, firebaseStorage } from "@/config/env";
import { useMessage, useNotification, useDialog } from "naive-ui";
import {
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    signInWithCustomToken,
    User, // User 타입을 임포트합니다.
    UserCredential,
} from "firebase/auth";
import * as common from "@/assets/common/common";
import * as constant from "@/assets/constant";
import { USER_ROUTES } from "@/assets/route_constants";
import { useAlertDialog } from "@/composables/useAlert";

const verifyAdminAndGrantAccessCallable = getCallable("verifyAdminAndGrantAccess");

export function useAuth() {
    const notification = useNotification();
    const store = useStore();
    const router = useRouter();
    const alertDialog = useAlertDialog();

    const loading = ref(false);
    const currentUser = ref<User | null>(null);

    const isLoggedIn = computed(() => {
        return !!currentUser.value;
    });

    const loggedInUserId = computed(() => {
        return currentUser.value?.email;
    });

    let unsubscribeAuthStateChanged: (() => void) | null = null;

    onMounted(() => {
        unsubscribeAuthStateChanged = firebaseAuth.onAuthStateChanged((user) => {
            currentUser.value = user;
        });
    });

    onUnmounted(() => {
        if (unsubscribeAuthStateChanged) {
            unsubscribeAuthStateChanged();
        }
    });

    async function signInWithGoogle() {
        loading.value = true;
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            const user = result.user;
            console.log("Admin Google Sign-In successful for:", user.email);

            const verificationResult: any = await verifyAdminAndGrantAccessCallable();
            if (verificationResult.data.message == "success" && verificationResult.data.data.isAdmin) {
                console.log("Admin verification successful:", verificationResult.data.message);
                await user.getIdToken(true);
                router.push({ name: USER_ROUTES.Home.Name });
            } else {
                console.error("Admin verification failed:", verificationResult.data.message);
                if (firebaseAuth.currentUser) {
                    await firebaseAuth.signOut();
                }
                notification.error({
                    title: "에러",
                    description: `관리자 인증 실패: ${verificationResult.data.message || "오류가 발생했습니다."}`,
                    duration: 1500,
                    closable: true,
                });
            }
        } catch (error: any) {
            if (firebaseAuth.currentUser) {
                await firebaseAuth.signOut();
            }
            console.error("Admin Google Sign-In or verification error:", error);
            if (
                error.code === "functions/unauthenticated" ||
                (error.details && error.details.code === "PERMISSION_DENIED")
            ) {
                notification.error({
                    title: "에러",
                    description: `로그인 한 계정은 관리자 권한이 없습니다.`,
                    duration: 1500,
                    closable: true,
                });
            } else {
                notification.error({
                    title: "에러",
                    description: `로그인 시도 중 오류가 발생했습니다.`,
                    duration: 1500,
                    closable: true,
                });
            }
        } finally {
            loading.value = false;
        }
    }
    async function handleLogout(showAlert: boolean = true) {
        loading.value = true;
        try {
            await signOut(firebaseAuth);

            if (showAlert) {
                await alertDialog("로그아웃 되었습니다.");

                router.push({ name: USER_ROUTES.Login.Name });
            }
        } catch (error: any) {
            console.error("Logout Error:", error);
            if (showAlert) await alertDialog("로그아웃 중 오류가 발생했습니다.");
        } finally {
            loading.value = false;
        }
    }

    return {
        loading,
        isLoggedIn,
        loggedInUserId,
        signInWithGoogle,
        handleLogout,
    };
}
